/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Modal, Space, Tag } from 'antd';
import {EditOutlined } from '@ant-design/icons';
import { useUserList } from '../../hooks/users/useUsersList';
import useListFilters from '../../hooks/useListFilters';
import ListPage from '../core/ListPage';
import EditUser from './EditUser';
import AddUser from './AddUsers';
import { mapModulesString } from '../../utils/staff';
import { ADMIN_MODULES } from '../../axios/constants';
import UserInvitationDelete from './UserInvitationDelete';
const BREAD_CRUMBS = [
  { label: 'Staff', to: '/user/userslist' },
  { label: 'List', to: '/user/userslist' },
];

const UsersList = () => {
  const formRef = useRef(null);
  const { data, getUsersList } = useUserList();
  const {
    loading,
    search,
    tableParams,
    setLoading,
    setSearch,
    setTableParams,
  } = useListFilters();
  const [editRecord, setEditRecord] = useState();
  const [show, setShow] = useState(false);

  // modal
  const showModal = () => setShow(true);
  const hideModal = () => {
    formRef?.current?.reset?.();
    setShow(false);
    setEditRecord(undefined);
  };
  const handleCancel = () => {
    hideModal();
  };
  const handleSuccess = () => {
    hideModal();
    reload();
  };
  const handleError = () => {};

  // Table
  const handleAdd = () => showModal();
  const handleEdit = (record) => {
    if (record?.associationType !== 'clinicUser') return;
    showModal();
    let data;
    data = {
      userSub: record?.sub,
      firstName: record?.firstName,
      lastName: record?.lastName,
      nameTitle: record?.nameTitle,
      email: record?.email,
      modules: record.associatedClinicLocationIsAdmin? ['CASE','ACCOUNT','REPORT'] :mapModulesString(record?.associatedClinicLocationModules),
      status: record?.associatedClinicUserStatus,
      isAdmin: record?.associatedClinicLocationIsAdmin,
    };

    setEditRecord(data);
  };

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getUsersList({
        search,
        page: tableParams.pagination?.current,
        limit: tableParams.pagination?.pageSize,
        sortOrder: tableParams?.sortOrder,
        sortBy: tableParams?.sortField,
      });
      setLoading(false);
      // Update table params
      setTableParams({
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          total: response.totalItems,
        },
      });
    } catch (error) {
      setLoading(false);
    }
  }, [
    tableParams.pagination?.current,
    tableParams.pagination?.pageSize,
    tableParams?.sortOrder,
    tableParams?.sortField,
    search,
  ]);
  const handleTableChange = (pagination, _, sorter) => {
    setTableParams({
      pagination,
      sortOrder: Array.isArray(sorter) ? undefined : sorter.order,
      sortField: Array.isArray(sorter) ? undefined : sorter.field,
    });
  };

  useEffect(() => {
    reload();
  }, [
    tableParams.pagination?.current,
    tableParams.pagination?.pageSize,
    tableParams?.sortOrder,
    tableParams?.sortField,
    search,
  ]);
  const actionItems = [{ key: 'Edit', label: 'Edit' }];

  const columns = [
    {
      title: 'No.',
      key: 'index',
      render: (_, record, index) => {
        const currentPage = tableParams.pagination?.current || 1;
        const pageSize = tableParams.pagination?.pageSize || 10;
        return (currentPage - 1) * pageSize + index + 1;
      },
    },
    {
      title: 'Name',
      dataIndex: 'fullName',
      sorter: true,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      sorter: true,
    },
    {
      title: 'Registered',
      dataIndex: 'associationType',
      render: (_, { associationType }, index) => {
        let color = associationType == 'clinicUser' ? 'green' : 'geekblue';
        let status = associationType == 'clinicUser' ? 'Done' : 'Pending';
        return (
          <Tag color={color} key={status}>
            {status.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Module',
      dataIndex: 'inviteClinicLocationRequestModules',
      render: (_, record, index) => {
        if (record?.associationType === 'clinicUser') {
          if (record?.associatedClinicLocationIsAdmin) {
            return ADMIN_MODULES;
          }
          return record?.associatedClinicLocationModules;
        }
        return record?.inviteClinicLocationRequestModules;
      },
    },
    {
      title: 'Status',
      key: 'associatedClinicUserStatus',
      dataIndex: 'associatedClinicUserStatus',
      render: (_, { associatedClinicUserStatus, associationType }, index) => {
        let color = associatedClinicUserStatus ? 'green' : 'geekblue';
        let status = associatedClinicUserStatus ? 'Active' : 'InActive';

        if (associationType === 'clinicUser') {
          return (
            <Tag color={color} key={status}>
              {status}
            </Tag>
          );
        }
        return '-';
      },
    },
    {
      title: '',
      dataIndex: '',
      render: (text, record) => (
        <Space size="middle">
          <Button
            key="ok"
            type="primary"
            disabled={record?.associationType !== 'clinicUser'}
            onClick={() => handleEdit(record)}
            icon={<EditOutlined />}
          />
          <UserInvitationDelete
            record={record}
            onInvitationDelete={reload}
            onError={reload}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <ListPage
        title={'Staff List'}
        data={data?.items || []}
        columns={columns}
        loading={loading}
        pagination={tableParams.pagination}
        rowKey={(record) => record?.email}
        onAdd={handleAdd}
        onRefresh={reload}
        onSearch={setSearch}
        onTableChange={handleTableChange}
        breadCrumbItems={BREAD_CRUMBS}
      />
      <Modal
        title={editRecord ? 'Edit' : 'Add'}
        open={show}
        onCancel={handleCancel}
        maskClosable={false}
        footer={null}
      >
        {editRecord ? (
          <EditUser
            ref={formRef}
            formValues={editRecord ?? {}}
            onSuccess={handleSuccess}
            onError={handleError}
          />
        ) : (
          <AddUser
            ref={formRef}
            onSuccess={handleSuccess}
            onError={handleError}
          />
        )}
      </Modal>
    </>
  );
};

export default UsersList;
